import type { LinkedMerchandisedProductType, LinkedSizeAttributesType } from 'src/types/decorationSpecs';
import { getSdk } from 'src/sdk';

/**
 * TODO(linked-product-fallback): this entire module exists only because some CIMs don't carry
 * Width/Length themselves. If/when CIM products carry this data directly (tracked informally, not
 * in a ticket as of this writing), delete this file outright and remove every other piece tagged
 * `TODO(linked-product-fallback)` (`productPropertySizeChart.ts`'s `productHasSizeOption`/
 * `productDeclaresBodyMeasurement`/`buildSizeChartFromLinkedAttributes`/
 * `resolveLinkedBodyWidthMm`/`resolveLinkedBodyLengthMm`; `decorationSpecs/saga.ts`'s
 * `fetchLinkedProductIfNeeded`/`fetchLinkedSizeAttributesIfNeeded` and their wiring;
 * `decorationSpecs/slice.ts`'s `linkedProduct`/`linkedSizeAttributesKey`/`linkedSizeAttributes`
 * state and their actions; `getDecorationSpecs.ts`'s fallback branches;
 * `types/decorationSpecs.ts`'s `LinkedMerchandisedProductType`/`LinkedSizeAttributesType`) —
 * `grep -r "TODO(linked-product-fallback)"` finds all of them.
 *
 * Fallback source for a CIM's body Width/Length when the CIM's own product definition doesn't
 * declare them (see `productPropertySizeChart.ts`'s module doc). Newer CIMs can omit Width/Length
 * entirely from their own options/properties — that data only exists on a merchandised (PRD)
 * product built on top of the CIM. This module resolves which merchandised product that is, and
 * reads Width/Length off it for one specific size.
 *
 * `api.products.cimpress.io` (what `productModelClient.ts` uses for the CIM itself) only accepts
 * CIM ids — confirmed live, it 404s for every PRD id tried — so a different pair of hosts is
 * needed here:
 * - Product Search (`product-orchestration.products.cimpress.io/v1/products:search`) to find the
 *   PRD, and that same host's `/v1/products/{prdId}` to learn its current version.
 * - Selector API (`selector-api.products.cimpress.io`) to resolve Width/Length for a specific
 *   size. This is the one authoritative source for these numbers — confirmed live, and matches
 *   this repo's own prior investigation into where Margay's size-chart numbers actually came
 *   from (see the "Replacing Margay" revision in the apparel-positioning summary doc). A
 *   different, simpler-looking PRD endpoint (`product-orchestration`'s own
 *   `/v1/products/{prdId}` property list) also returns Width/Length values, but as a flat array
 *   with no condition/size tag at all — live-checked, and the values don't line up with the
 *   `Size` option's own order (a smaller size can come out "wider" than a larger one), so that
 *   array's ordering is not safe to guess at and is deliberately not used here.
 */

/**
 * Resolves the first active merchandised (PRD) product built on top of `cimId`, and its current
 * version. Resolves to `null` when no such product is found (e.g. a draft CIM with no
 * merchandising yet).
 */
export async function findLinkedMerchandisedProduct(cimId: string): Promise<LinkedMerchandisedProductType | null> {
    if (!cimId) {
        return null;
    }

    const searchResponse = await getSdk().Products.searchProduct({
        fullTextSearch: cimId,
        productFeatures: ['isMerchandised'],
        statuses: ['Active'],
        limit: 50,
        offset: '0',
        shouldAggregate: true,
        showExactTotalCount: true,
    });

    const items = searchResponse?._embedded?.items ?? [];
    // Checks every entry in `matchingVersions`, not just the first — a PRD can have more than one
    // (e.g. its root-CIM mapping changed between versions, as this repo's own "Vista PPAG PRD
    // repointing to Standard adoptions" migration does), and the entry that actually matches
    // `cimId` isn't guaranteed to be at index 0. Checking only `[0]` would wrongly report "no
    // linked product" for a PRD that genuinely is linked, just via a later entry.
    const match = items.find(
        (item) =>
            typeof item.productId === 'string' &&
            item.productId.startsWith('PRD-') &&
            item.matchingVersions?.some((matchingVersion) => matchingVersion.rootProduct?.productId === cimId),
    );

    if (!match?.productId) {
        return null;
    }

    // The search result's own `matchingVersions[0].version` is whichever version matched the
    // search, not necessarily this PRD's *current* version — re-fetch to get the version
    // Selector API should actually be queried against.
    const productResponse = await getSdk()
        .Products.getProductDefinition(match.productId)
        .catch(() => null);

    const version = productResponse?.version;
    if (version === undefined) {
        return null;
    }

    return { productId: match.productId, version };
}

/**
 * Resolves Width/Length for `size` on `linkedProduct` via Selector API's condition-respecting
 * resolution. Omits either field when Selector API doesn't resolve a value for it (e.g. this
 * merchandised product doesn't declare that measurement either).
 *
 * Selector API reports an attribute's unit two different ways depending on the merchandised
 * product: sometimes embedded in `resolvedValue` itself (e.g. `"812.8mm"`), sometimes as this
 * separate `unitOfMeasure` field alongside a bare numeric `resolvedValue` (e.g. `"74"` +
 * `unitOfMeasure: "cm"` — confirmed live on `PRD-B63H6BVG`). Both are returned here (`widthUnit`/
 * `lengthUnit`) so callers can pass whichever one actually carries the unit through to `toMm` —
 * leaving `unitOfMeasure` unread, as a prior version of this function did, meant `toMm` fell
 * through to its inches default for the separate-field case, silently misconverting (e.g. "74cm"
 * read as "74 inches").
 */
export async function resolveLinkedSizeAttributes(
    linkedProduct: LinkedMerchandisedProductType,
    size: string,
): Promise<Omit<LinkedSizeAttributesType, 'size'>> {
    const response = await getSdk().Selector.validateProductVersionSelections({
        productId: linkedProduct.productId,
        version: linkedProduct.version,
        data: { selections: { Size: size } },
    });

    const attributes = response?.attributes ?? [];
    const find = (key: string) => attributes.find((attribute) => attribute.key === key);

    const width = find('Width');
    const length = find('Length');

    return {
        widthRaw: width?.resolvedValue,
        widthUnit: width?.unitOfMeasure,
        lengthRaw: length?.resolvedValue,
        lengthUnit: length?.unitOfMeasure,
    };
}
